import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
// import AdBanner from "../components/AdBanner";
import CustomSpinner from "../components/CustomSpinner";
import LandingContent from "../components/LandingContent";
import LoginComponent from "../components/LoginComponent";
import UserContext from "../context/UserContext";
import transactionService from "../services/transactionService";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "MXN",
  }).format(amount ?? 0);

const formatDate = (value) => {
  if (!value) return "-";

  const dateString = value.split("T")[0];

  const date = new Date(dateString.replace(/-/g, "/"));

  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("es-ES");
};

const getCurrentMonth = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${now.getFullYear()}-${month}`;
};

const getMonthDateRange = () => {
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth(), 1)
    .toISOString()
    .split("T")[0];
  const endDate = now.toISOString().split("T")[0];
  return { startDate, endDate };
};

const getCurrentFortnightRange = () => {
  const now = new Date();
  const startDay = now.getDate() <= 15 ? 1 : 16;
  const startDate = new Date(now.getFullYear(), now.getMonth(), startDay)
    .toISOString()
    .split("T")[0];
  const endDate = now.toISOString().split("T")[0];
  return { startDate, endDate };
};

const buildSummary = (transactions) => {
  const totalIncome = transactions
    .filter((transaction) => transaction.type === "income")
    .reduce((total, transaction) => total + Number(transaction.amount || 0), 0);
  const categoryTotals = transactions.reduce((categories, transaction) => {
    if (transaction.type === "expense") {
      const category = categories[transaction.categoryId] || {
        categoryId: transaction.categoryId,
        name: transaction.categoryName || "Sin categoría",
        amount: 0,
      };
      category.amount += Number(transaction.amount || 0);
      categories[transaction.categoryId] = category;
    }
    return categories;
  }, {});
  const byCategory = Object.values(categoryTotals).sort((a, b) => b.amount - a.amount);
  const totalExpense = byCategory.reduce((total, category) => total + category.amount, 0);

  return { totalIncome, totalExpense, balance: totalIncome - totalExpense, byCategory };
};

const CHART_COLORS = [
  "#E24B4A", "#D85A30", "#BA7517", "#639922", "#1D9E75",
  "#185FA5", "#7F77DD", "#D4537E", "#888780", "#534AB7",
];

export default function Dashboard() {
  const { user, loading, setLoading } = useContext(UserContext);
  const navigate = useNavigate();

  const [summary, setSummary] = useState({
    totalIncome: 0,
    totalExpense: 0,
    balance: 0,
    byCategory: [],
  });
  const [totalSavings, setTotalSavings] = useState(0);
  const [maxCategoryAmount, setMaxCategoryAmount] = useState(1);
  const [period, setPeriod] = useState("fortnight");

  const [expenseCategories, setExpenseCategories] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const month = getCurrentMonth();
    const { startDate, endDate } = period === "month"
      ? getMonthDateRange()
      : getCurrentFortnightRange();

    const fetchData = async () => {
      setLoading(true);
      try {
        const [summaryRes, transactionsRes, savingsRes] = await Promise.all([
          period === "month" ? transactionService.getSummary(month) : Promise.resolve(null),
          transactionService.getTransactions(startDate, endDate),
          transactionService.getTotalSavings(),
        ]);

        const nextSummary = period === "month"
          ? summaryRes.data
          : buildSummary(transactionsRes.data);
        setSummary(nextSummary);
        setExpenseCategories(nextSummary.byCategory);
        setMaxCategoryAmount(
          Math.max(
            ...nextSummary.byCategory.map((item) => item.amount),
            1
          )
        );
        setTotalSavings(savingsRes.data.totalSavings);

        setRecentTransactions(transactionsRes.data.slice(0, 5));
      } catch (error) {
        console.error("Error cargando dashboard:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, period, setLoading]);

  useEffect(() => {
    if (!user) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      // AdSense no disponible en desarrollo
    }
  }, [user]);

  // un bg muy chido 
  // bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.12),_transparent_50%)]
  return (
    <>
      {loading && <CustomSpinner />}
      {user === null ? (
        <div className="min-h-[91vh] banner-1 px-4 py-5 sm:px-6 lg:px-8">
          <div className="mx-auto flex max-w-6xl justify-center">
            <LoginComponent />
          </div>
          <LandingContent />
        </div>
      ) : (
        <div className="min-h-[91vh] banner-1
        
         px-4 py-6 text-slate-100 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl rounded-3xl border border-white/10 bg-slate-900/80 p-4 shadow-2xl shadow-black/20 sm:p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-xl font-semibold">
                {period === "month" ? "Resumen del mes" : "Resumen de la quincena"}
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex rounded-xl bg-slate-800 p-1 text-sm ring-1 ring-white/10">
                  <button
                    onClick={() => setPeriod("month")}
                    className={`rounded-lg px-3 py-1.5 transition ${period === "month" ? "bg-sky-500 text-white" : "text-slate-300 hover:bg-slate-700"}`}
                  >
                    Mes
                  </button>
                  <button
                    onClick={() => setPeriod("fortnight")}
                    className={`rounded-lg px-3 py-1.5 transition ${period === "fortnight" ? "bg-sky-500 text-white" : "text-slate-300 hover:bg-slate-700"}`}
                  >
                    Quincena actual
                  </button>
                </div>
                <button
                  onClick={() => navigate("/transactions", { state: { openForm: true } })}
                  className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm font-medium text-emerald-300 transition hover:bg-emerald-500/20"
                >
                  + Nueva transacción
                </button>
              </div>
            </div>

            <div className="mb-3 grid gap-3 md:grid-cols-3">
              <div className="rounded-2xl bg-emerald-500/15 p-4 ring-1 ring-emerald-400/25">
                <div className="text-sm text-emerald-200">Ingresos del mes</div>
                <div className="mt-2 text-2xl font-semibold">{formatCurrency(summary.totalIncome)}</div>
              </div>
              <div className="rounded-2xl bg-rose-500/15 p-4 ring-1 ring-rose-400/25">
                <div className="text-sm text-rose-200">Gastos del mes</div>
                <div className="mt-2 text-2xl font-semibold">{formatCurrency(summary.totalExpense)}</div>
              </div>
              <div className="rounded-2xl bg-sky-500/15 p-4 ring-1 ring-sky-400/25">
                <div className="text-sm text-sky-200">Balance</div>
                <div className={`mt-2 text-2xl font-semibold ${summary.balance < 0 ? "text-rose-400" : "text-white"}`}>
                  {formatCurrency(summary.balance)}
                </div>
              </div>
            </div>

            <div className="mb-4 rounded-2xl bg-slate-800/70 p-4 ring-1 ring-white/10">
              <div className="text-sm text-slate-300">Ahorros totales</div>
              <div className={`mt-2 text-2xl font-semibold ${totalSavings < 0 ? "text-rose-400" : "text-white"}`}>
                {formatCurrency(totalSavings)}
              </div>
            </div>

            <div className="mb-4">
              <div className="mb-3 flex items-center justify-between">
                <h4 className="text-lg font-semibold">Últimas transacciones</h4>
                <button onClick={() => navigate("/transactions")} className="text-sm font-medium text-sky-300 transition hover:text-sky-200">
                  Ver todas
                </button>
              </div>

              <div className="hidden overflow-hidden rounded-2xl border border-white/10 sm:block">
                <div className="overflow-x-auto">
                  <table className="min-w-[560px] w-full divide-y divide-white/10 text-sm">
                    <thead className="bg-slate-800/80 text-left text-slate-300">
                      <tr>
                        <th className="px-3 py-3">Fecha</th>
                        <th className="px-3 py-3">Descripción</th>
                        <th className="px-3 py-3">Categoría</th>
                        <th className="px-3 py-3 text-right">Monto</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10 bg-slate-900/50">
                      {recentTransactions.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-3 py-4 text-center text-slate-400">
                            No hay transacciones este mes
                          </td>
                        </tr>
                      ) : (
                        recentTransactions.map((tx) => (
                          <tr key={tx.id}>
                            <td className="px-3 py-3">{formatDate(tx.date)}</td>
                            <td className="px-3 py-3">{tx.description || "-"}</td>
                            <td className="px-3 py-3">{tx.categoryName || "-"}</td>
                            <td className={`px-3 py-3 text-right font-semibold ${tx.type === "income" ? "text-emerald-400" : "text-rose-400"}`}>
                              {tx.type === "expense" ? "-" : "+"}
                              {formatCurrency(tx.amount)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="space-y-3 sm:hidden">
                {recentTransactions.length === 0 ? (
                  <div className="rounded-2xl border border-white/10 bg-slate-900/50 px-3 py-4 text-center text-sm text-slate-400">
                    No hay transacciones este mes
                  </div>
                ) : (
                  recentTransactions.map((tx) => (
                    <div key={tx.id} className="rounded-2xl border border-white/10 bg-slate-900/60 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-medium text-slate-100">{tx.description || "-"}</div>
                          <div className="mt-1 text-sm text-slate-400">{formatDate(tx.date)}</div>
                        </div>
                        <div className={`text-right font-semibold ${tx.type === "income" ? "text-emerald-400" : "text-rose-400"}`}>
                          {tx.type === "expense" ? "-" : "+"}
                          {formatCurrency(tx.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm text-slate-400">{tx.categoryName || "-"}</div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div>
              <h4 className="mb-3 text-lg font-semibold">Gastos por categoría</h4>
              {expenseCategories.length > 0 && (
                <div className="mb-4 grid items-center gap-4 lg:grid-cols-[1.1fr_0.9fr]">
                  <div className="h-60 rounded-2xl border border-white/10 bg-slate-800/60 p-3">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie key={period} data={expenseCategories} dataKey="amount" nameKey="name" cx="50%" cy="50%" innerRadius="60%" outerRadius="85%" paddingAngle={3} strokeWidth={0}>
                          {expenseCategories.map((_, idx) => (
                            <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [formatCurrency(value), name]} contentStyle={{ background: "#111827", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, color: "#fff", fontSize: 13 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex flex-col gap-2">
                    {expenseCategories.map((item, idx) => {
                      const pct = Math.round((item.amount / summary.totalExpense) * 100);
                      return (
                        <div key={item.categoryId} className="flex items-center gap-2 rounded-xl bg-slate-800/60 px-3 py-2">
                          <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }} />
                          <span className="flex-1 text-sm text-slate-200">{item.name}</span>
                          <span className="min-w-[2rem] text-right text-sm text-slate-400">{pct}%</span>
                          <span className="min-w-[5.5rem] text-right text-sm font-medium text-slate-100">{formatCurrency(item.amount)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {summary.byCategory.length === 0 ? (
                <p className="text-sm text-slate-400">Sin gastos registrados este mes</p>
              ) : (
                summary.byCategory.map((item) => (
                  <div key={item.categoryId} className="mb-3">
                    <div className="mb-1 flex items-center justify-between text-sm text-slate-300">
                      <span>{item.name}</span>
                      <span>{formatCurrency(item.amount)}</span>
                    </div>
                    <div className="h-2.5 overflow-hidden rounded-full bg-slate-800">
                      <div className="h-full rounded-full bg-sky-500 transition-all" style={{ width: `${(item.amount / maxCategoryAmount) * 100}%` }} />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}